import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const STATUS_LABEL = {
  present: 'Present', absent: 'Absent', half_day: 'Half Day',
  on_leave: 'On Leave', permission: 'Permission', holiday: 'Holiday',
};

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatMins(mins) {
  if (!mins || mins <= 0) return '—';
  return `${Math.floor(mins / 60)}h ${Math.round(mins % 60)}m`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function sessionsText(sessions = []) {
  if (!sessions.length) return '—';
  return sessions.map((s, i) =>
    `#${i + 1}: ${formatTime(s.clockIn)} → ${s.clockOut ? formatTime(s.clockOut) : 'Ongoing'}`
  ).join('  |  ');
}

// Build flat row array for a list of records
function buildRows(records) {
  return records.map(r => ({
    Employee: r.user?.name || '—',
    Email: r.user?.email || '—',
    Date: formatDate(r.attendanceDate),
    Status: STATUS_LABEL[r.status] || r.status,
    'First In': r.sessions?.[0]?.clockIn ? formatTime(r.sessions[0].clockIn) : '—',
    'Last Out': (() => {
      const last = r.sessions?.[r.sessions.length - 1];
      return last?.clockOut ? formatTime(last.clockOut) : '—';
    })(),
    'Total Hours': formatMins(r.totalMinutes),
    'Break Time': r.permissionMinutes > 0 ? `${Math.round(r.permissionMinutes)}m` : '—',
    Sessions: sessionsText(r.sessions),
    Remarks: r.remarks || '—',
  }));
}

// ── Excel export ──────────────────────────────────────────────────────────────
export function exportExcel(records, filename = 'Attendance_Report') {
  const rows = buildRows(records);
  const ws = XLSX.utils.json_to_sheet(rows);

  // Column widths
  ws['!cols'] = [
    { wch: 22 }, { wch: 28 }, { wch: 14 }, { wch: 12 },
    { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 },
    { wch: 50 }, { wch: 30 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${filename}.xlsx`);
}

// ── PDF export ────────────────────────────────────────────────────────────────
export function exportPDF(records, title = 'Attendance Report', subtitle = '') {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

  // Header
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text('Creative Glass Tec', 40, 40);

  doc.setFontSize(13);
  doc.setTextColor(30, 58, 138);
  doc.text(title, 40, 60);

  if (subtitle) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(subtitle, 40, 76);
  }

  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 40, subtitle ? 90 : 76);

  const STATUS_COLORS = {
    Present:    [240, 253, 244],
    Absent:     [255, 241, 242],
    'Half Day': [255, 251, 235],
    'On Leave': [245, 243, 255],
    Permission: [239, 246, 255],
    Holiday:    [255, 247, 237],
  };

  const rows = buildRows(records);
  const cols = ['Employee', 'Date', 'Status', 'First In', 'Last Out', 'Total Hours', 'Break Time', 'Remarks'];

  autoTable(doc, {
    startY: subtitle ? 100 : 86,
    head: [cols],
    body: rows.map(r => cols.map(c => r[c] || '—')),
    styles: { fontSize: 8, cellPadding: 5, valign: 'middle', overflow: 'linebreak' },
    headStyles: {
      fillColor: [30, 58, 138], textColor: 255, fontStyle: 'bold', fontSize: 8,
    },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 70 },
      2: { cellWidth: 65 },
      3: { cellWidth: 55 },
      4: { cellWidth: 55 },
      5: { cellWidth: 65 },
      6: { cellWidth: 60 },
      7: { cellWidth: 'auto' },
    },
    didParseCell(data) {
      if (data.section === 'body' && data.column.index === 2) {
        const statusVal = data.cell.raw;
        const bg = STATUS_COLORS[statusVal];
        if (bg) data.cell.styles.fillColor = bg;
      }
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 40, right: 40 },
  });

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 80, doc.internal.pageSize.height - 20);
    doc.text('Creative Glass Tec — Attendance Report', 40, doc.internal.pageSize.height - 20);
  }

  doc.save(`${title.replace(/\s+/g, '_')}.pdf`);
}

// ── Per-user summary Excel (multiple sheets, one per user) ───────────────────
export function exportExcelByUser(records, filename = 'Attendance_By_User') {
  const wb = XLSX.utils.book_new();

  // Group by user
  const byUser = {};
  records.forEach(r => {
    const name = r.user?.name || 'Unknown';
    if (!byUser[name]) byUser[name] = [];
    byUser[name].push(r);
  });

  // Summary sheet
  const summaryRows = Object.entries(byUser).map(([name, recs]) => ({
    Employee: name,
    Email: recs[0]?.user?.email || '—',
    'Total Days': recs.length,
    Present: recs.filter(r => r.status === 'present').length,
    Absent: recs.filter(r => r.status === 'absent').length,
    'Half Day': recs.filter(r => r.status === 'half_day').length,
    'On Leave': recs.filter(r => r.status === 'on_leave').length,
    Permission: recs.filter(r => r.status === 'permission').length,
    Holiday: recs.filter(r => r.status === 'holiday').length,
    'Total Hours': formatMins(recs.reduce((s, r) => s + (r.totalMinutes || 0), 0)),
  }));
  const summaryWs = XLSX.utils.json_to_sheet(summaryRows);
  summaryWs['!cols'] = [{ wch: 22 }, { wch: 28 }, ...Array(8).fill({ wch: 12 })];
  XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

  // One sheet per user
  Object.entries(byUser).forEach(([name, recs]) => {
    const rows = buildRows(recs);
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 22 }, { wch: 28 }, { wch: 14 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 50 }, { wch: 30 }];
    // Sheet name max 31 chars, strip special chars
    const sheetName = name.replace(/[\\/*?:[\]]/g, '').slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${filename}.xlsx`);
}

// ── Per-user summary PDF ──────────────────────────────────────────────────────
export function exportPDFByUser(records, title = 'Attendance Report by Employee', subtitle = '') {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

  const byUser = {};
  records.forEach(r => {
    const name = r.user?.name || 'Unknown';
    if (!byUser[name]) byUser[name] = [];
    byUser[name].push(r);
  });

  let firstPage = true;

  Object.entries(byUser).forEach(([name, recs]) => {
    if (!firstPage) doc.addPage();
    firstPage = false;

    // Employee header
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Creative Glass Tec', 40, 40);

    doc.setFontSize(12);
    doc.setTextColor(30, 58, 138);
    doc.text(`${title} — ${name}`, 40, 58);

    if (subtitle) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(subtitle, 40, 74);
    }

    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 40, subtitle ? 88 : 74);

    // Summary bar for this user
    const counts = {
      Present: recs.filter(r => r.status === 'present').length,
      Absent: recs.filter(r => r.status === 'absent').length,
      'Half Day': recs.filter(r => r.status === 'half_day').length,
      'On Leave': recs.filter(r => r.status === 'on_leave').length,
      Holiday: recs.filter(r => r.status === 'holiday').length,
    };
    const totalHours = formatMins(recs.reduce((s, r) => s + (r.totalMinutes || 0), 0));
    const summaryText = Object.entries(counts).map(([k, v]) => `${k}: ${v}`).join('   |   ') + `   |   Total Hours: ${totalHours}`;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text(summaryText, 40, subtitle ? 104 : 90);

    const rowData = buildRows(recs);
    const cols = ['Date', 'Status', 'First In', 'Last Out', 'Total Hours', 'Break Time', 'Remarks'];

    autoTable(doc, {
      startY: subtitle ? 114 : 100,
      head: [cols],
      body: rowData.map(r => cols.map(c => r[c] || '—')),
      styles: { fontSize: 8, cellPadding: 5, valign: 'middle' },
      headStyles: { fillColor: [30, 58, 138], textColor: 255, fontStyle: 'bold', fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 40, right: 40 },
    });
  });

  // Page numbers
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 80, doc.internal.pageSize.height - 20);
    doc.text('Creative Glass Tec — Attendance Report', 40, doc.internal.pageSize.height - 20);
  }

  doc.save(`${title.replace(/\s+/g, '_')}.pdf`);
}
